<template>
  <div class="flex flex-col gap-4">
    <TaskGroupSection
      v-for="group in groups"
      :key="groupCollapseKey(group.key)"
      :group="group"
      :show-header="showGroupHeaders"
      @toggle-collapse="$emit('toggle-collapse', group)"
      @load-more="$emit('load-more', group)"
    >
      <div class="overflow-x-auto rounded-md border">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 dark:bg-slate-700">
            <tr>
              <th v-if="selection" class="w-8 px-2 py-1.5">
                <input
                  type="checkbox"
                  data-test="group-select-all"
                  class="h-4 w-4 cursor-pointer accent-primary"
                  :checked="groupSelectionState(group) === 'all'"
                  :indeterminate="groupSelectionState(group) === 'some'"
                  :aria-label="$t('button.select')"
                  @change="selection.toggleGroup(selectableIdsOf(group))"
                />
              </th>
              <th
                v-for="column in listColumns"
                :key="column.id"
                class="px-2 py-1.5 text-left font-semibold select-none"
                :class="{ 'cursor-pointer hover:underline': column.sortable }"
                :tabindex="column.sortable ? 0 : undefined"
                :role="column.sortable ? 'button' : undefined"
                :aria-sort="ariaSortOf(column)"
                @click="handleHeaderClick(column)"
                @keydown.enter.prevent="handleHeaderClick(column)"
                @keydown.space.prevent="handleHeaderClick(column)"
              >
                {{ column.name }}
                <span v-if="primarySort?.column === column.id">
                  {{ primarySort.direction === 'asc' ? '▲' : '▼' }}
                </span>
              </th>
              <th v-if="$can('manage_dossier')" class="w-10" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="task in group.task_dossiers"
              :key="task.id"
              class="border-t"
              :class="{
                'cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-600':
                  selection,
                'bg-primary-100 dark:bg-slate-600': selection?.isSelected(
                  task.id
                )
              }"
              @click="selectRow(task, $event)"
            >
              <td v-if="selection" class="w-8 px-2 py-1.5">
                <input
                  type="checkbox"
                  data-test="row-select"
                  class="h-4 w-4 cursor-pointer accent-primary"
                  :checked="selection.isSelected(task.id)"
                  :aria-label="$t('button.select')"
                  @change="selection.toggle(task.id)"
                />
              </td>
              <td
                v-for="column in listColumns"
                :key="column.id"
                class="px-2 py-1.5"
              >
                <div
                  v-if="column.id === 'status'"
                  class="flex items-center gap-1 whitespace-nowrap"
                >
                  <button
                    v-if="statusClickCycle"
                    type="button"
                    data-test="status-pill"
                    :data-tint="`task-status-${task.status}`"
                    class="task-gradient flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium"
                    :disabled="!$can('manage_dossier')"
                    @click.stop="cycleStatus(task)"
                  >
                    <FontAwesomeIcon
                      :icon="['fal', statusOf(task.status)?.icon ?? 'circle']"
                    />
                    <span>{{ $t('enums.task.status.' + task.status) }}</span>
                  </button>
                  <SelectMiniStatusTask
                    v-else
                    data-test="status-select"
                    :model-value="task.status"
                    :disabled="!$can('manage_dossier')"
                    @value-changed="updateStatus(task, $event)"
                  />
                </div>
                <template v-else>{{ cellValue(task, column.id) }}</template>
              </td>
              <td
                v-if="$can('manage_dossier')"
                class="w-10 px-1 py-1.5 text-right"
              >
                <BaseDropDownMenu
                  data-test="task-menu"
                  :options="menuOptionsOf(task)"
                  size="medium"
                  :is-button="false"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <template #loading-more>
        <div class="flex flex-col gap-2">
          <BaseSkeleton :count="3">
            <BaseSkeletonBar height="h-8" class="dark:bg-slate-600" />
          </BaseSkeleton>
        </div>
      </template>
    </TaskGroupSection>
    <TaskDossierModal
      v-model:open="taskModalOpened"
      v-model:edition-mode="taskModalEditionMode"
      :task-id="selectedTaskId"
      :is-task-dossier="true"
      @refresh-list="$emit('refresh-list')"
    />
    <ActivityLogModal
      v-model:open="activityLogModalOpened"
      :model-id="selectedTaskId ?? ''"
      model-type="task-dossier"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseSkeleton from '@c/BaseSkeleton.vue'
import BaseSkeletonBar from '@c/BaseSkeletonBar.vue'
import BaseDropDownMenu from '@c/addf-package/components/dropdown/BaseDropDownMenu.vue'
import TaskDossierModal from '@c/baseComponents/modal/taskDossier/TaskDossierModal.vue'
import ActivityLogModal from '@ui/modal/activityLog/ActivityLogModal.vue'
import SelectMiniStatusTask from '@/views/dossier/form/subpages/taskDossier/components/taskDossierList/components/SelectMiniStatusTask.vue'
import TaskGroupSection from '../components/TaskGroupSection.vue'
import { visibleListColumns } from '../utils/taskDashboardColumns'
import { groupCollapseKey } from '../utils/taskDashboardSettings'
import { formatTaskDate } from '../utils/taskDates'
import { TASK_STATUS_CYCLE } from '@/views/dossier/form/subpages/taskDossier/components/taskDossierList/components/taskStatusCycle'
import { taskDisplayName } from '@/views/dossier/form/subpages/taskDossier/components/taskDossierList/components/taskDisplayName'
import { taskDossierMenuOptions } from '@/views/dossier/form/subpages/taskDossier/components/taskDossierList/components/taskDossierMenuOptions'

export default {
  name: 'TaskListView',
  components: {
    ActivityLogModal,
    BaseDropDownMenu,
    BaseSkeleton,
    BaseSkeletonBar,
    SelectMiniStatusTask,
    TaskDossierModal,
    TaskGroupSection,
    FontAwesomeIcon
  },
  props: {
    selection: { type: Object, required: false, default: null },
    groups: { type: Array, required: true },
    settings: { type: Object, required: true },
    showGroupHeaders: { type: Boolean, required: true },
    statusClickCycle: { type: Boolean, required: false, default: true },
    /** Unused here: declared so the container prop shared by every view does not fall through as an attribute. */
    statusCounts: { type: Object, required: false, default: () => ({}) }
  },
  emits: [
    'toggle-collapse',
    'load-more',
    'sort-by-column',
    'task-status-updated',
    'task-deleted',
    'refresh-list'
  ],
  data() {
    return {
      selectedTaskId: null,
      taskModalOpened: false,
      taskModalEditionMode: false,
      activityLogModalOpened: false
    }
  },
  computed: {
    ...mapGetters({
      columns: 'metadata/getTaskDossierColumns',
      task_categories: 'metadata/getMetadataTaskCategories',
      task_statuses: 'metadata/getMetadataTaskStatuses',
      dueDateTypes: 'metadata/getTaskDueDateTypes'
    }),
    listColumns() {
      return visibleListColumns(this.settings.fields).map(
        (id) =>
          this.columns.find((column) => column.id === id) ?? {
            id,
            name: id,
            sortable: false
          }
      )
    },
    primarySort() {
      return this.settings.sort[0] ?? null
    }
  },
  methods: {
    groupCollapseKey,
    /**
     * A click anywhere on a row toggles its selection, except on its own controls
     * (checkbox, status, menu), which keep their action.
     */
    selectRow(task, event) {
      if (
        !this.selection ||
        event.target.closest('button, a, input, select, [role="menu"]')
      ) {
        return
      }
      this.selection.toggle(task.id)
    },
    /** null hides the select-all: nothing to select while the group is not loaded. */
    groupSelectionState(group) {
      const ids = this.selectableIdsOf(group)
      return this.selection && ids.length > 0
        ? this.selection.groupState(ids)
        : null
    },
    /** The rows a group's select-all covers: those loaded on screen. */
    selectableIdsOf(group) {
      return group.task_dossiers.map((task) => task.id)
    },
    ...mapActions({
      processUpdateDossierTaskStatusAction: 'dossier/updateDossierTaskStatus',
      processDeleteTaskDossierByIdAction: 'dossier/deleteTaskDossierById'
    }),
    handleHeaderClick(column) {
      if (!column.sortable) {
        return
      }
      this.$emit('sort-by-column', column.id)
    },
    translatedName(translations) {
      return taskDisplayName(translations, this.$h)
    },
    ariaSortOf(column) {
      if (this.primarySort?.column !== column.id) {
        return undefined
      }
      return this.primarySort.direction === 'asc' ? 'ascending' : 'descending'
    },
    statusOf(statusId) {
      return this.task_statuses.find((status) => status.id === statusId)
    },
    cycleStatus(task) {
      this.updateStatus(
        task,
        TASK_STATUS_CYCLE[
          (TASK_STATUS_CYCLE.indexOf(task.status) + 1) %
            TASK_STATUS_CYCLE.length
        ]
      )
    },
    updateStatus(task, newStatus) {
      if (newStatus === task.status) {
        return
      }
      this.$emit('task-status-updated', { taskId: task.id, newStatus })
      this.processUpdateDossierTaskStatusAction({
        id: task.id,
        new_status: newStatus
      }).catch(() => {
        this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        this.$emit('refresh-list')
      })
    },
    menuOptionsOf(task) {
      return taskDossierMenuOptions((key) => this.$t(key), {
        view: () => this.openTaskModal(task),
        edit: () => this.openTaskModal(task),
        activityLog: () => this.openActivityLog(task),
        delete: () => this.deleteTask(task)
      })
    },
    openTaskModal(task) {
      this.selectedTaskId = task.id
      this.taskModalEditionMode = false
      this.taskModalOpened = true
    },
    openActivityLog(task) {
      this.selectedTaskId = task.id
      this.activityLogModalOpened = true
    },
    async deleteTask(task) {
      const response = await this.processDeleteTaskDossierByIdAction({
        task_id: task.id
      })
      this.$emit('task-deleted', { id: response.id })
    },
    dueDateTypeLabel(dueDateType) {
      const withoutDueDate = this.$t('global.without_due_date')
      if (!dueDateType || dueDateType === 'none') {
        return withoutDueDate
      }
      return (
        this.dueDateTypes.find((option) => option.id === dueDateType)?.name ??
        dueDateType
      )
    },
    cellValue(task, columnId) {
      switch (columnId) {
        case 'task_name':
          return this.translatedName(task.name)
        case 'deceased':
          return task.dossier?.concernedPerson?.full_name ?? ''
        case 'item':
          return task.item?.name ?? task.parent_label ?? ''
        case 'category_id':
          return (
            this.task_categories.find(
              (category) => category.id === task.category_id
            )?.name ?? ''
          )
        case 'funeral_home_id':
          return task.dossier?.funeral_home_name ?? ''
        case 'company_id':
          return task.dossier?.company_name ?? ''
        case 'responsable':
          return task.users?.manager?.full_name ?? ''
        case 'assigned_person':
          return (task.users?.executors ?? [])
            .map((executor) => executor.full_name)
            .join(', ')
        case 'due_date_type':
          return this.dueDateTypeLabel(task.due_date_type)
        case 'due_date':
          return (
            formatTaskDate(task.due_date_time, task.due_date_undefined_time) ||
            '—'
          )
        case 'created_at':
          return formatTaskDate(task.created_at, false) || '—'
        case 'funeral_date':
          return (
            formatTaskDate(
              task.funeral_date,
              task.funeral_date_undefined_time
            ) || '—'
          )
        default:
          return ''
      }
    }
  }
}
</script>
